import { getAnalyticsConfig } from '@ynap/config-state'
import getCart, { getUserStatus } from './getCart'
import { getCurrentShippingAddress } from './getShippingAddressStep'
import { getCurrentBillingAddress, getCurrentPaymentOption, getCurrentPaymentList, getCardTypeInfo } from './getPaymentStep'
import { getCurrentPackagingOption } from './getPackagingOptionsStep'
import { getLocale, getCurrentShippingOption, getCurrentOrderShippingInfo } from '@ynap/cart-state'
import { CHKConfig, Messages, ShoppingCart } from 'app/types/common-types'
import { createCombinedSelector } from '../utils/selectorUtils'
import { StoreState } from 'app/state/types'

export default createCombinedSelector({
    name: 'getOrderConfirmation',
    state: ['locale', 'config', 'cart', 'deviceType', 'countries', 'donateToCharity', 'crisisMessages'],
    props: ['messages'],
    fn: getOrderConfirmationInternal,
})

type Props = {
    messages: Messages
}

export function getOrderConfirmationInternal(state: StoreState, props: Props) {
    const cart  = getCart(state, props)
    const {
        adjustment,
        lineItems,
        numItems,
        totals,
        genericTotal,
        saleItemsInOrder,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        promotionCode,
        donationAvailable,
        donationCharities,
        donationOrderId,
        isGift,
        hasJITItems,
        remoteItems,
        localItems,
        dduAcceptance,
        deliveryDuty,
        isPostSplit,
    } = cart as ShoppingCart

    const orderRef = getOrderRef(state)
    const shippingAddress = getCurrentShippingAddress(state)
    const [shippingOption, shippingOptionJit] = getCurrentShippingOption(state, props)
    const [shippingOptionInfo, shippingOptionInfoJit] = getCurrentOrderShippingInfo(state)

    const paymentOption = getCurrentPaymentOption(state)
    const cardTypeInfo = getCardTypeInfo(state)
    const billingAddress = getCurrentBillingAddress(state)
    const userStatus = getUserStatus(state)
    const deviceType = getDeviceType(state)
    const email = getUserEmail(state)
    const analytics = getAnalyticsConfig(state)
    const locale = getLocale(state)
    const currentPackagingOption = getCurrentPackagingOption(state, props)

    const {
        flags,
        links = {},
        buyapowa: buyapowaConfig,
        enableDonationGiving,
        gdprLikeCountries,
        ageConsentCountries,
        tcConsentCountries,
        orderConfirmationDevMode,
        minimumPriceCountries,
        discount15OffCountries,
        tierExpansionEnabled,
    } = state?.config?.head as CHKConfig ?? {}
    const { enableSustainability: sustainabilityEnabled, postSplitEnabled, enableGenderSelection } = flags ?? {}
    const { language, country } = locale ?? {}
    const paymentsOptionsList = getCurrentPaymentList(state)
    const donationStatus = getDonationStatus(state)
    const crisisMessages = state.crisisMessages
    const parsedLinks = Object.entries(links).reduce((accumulator, [type, link]) => {
        return { ...accumulator, ...{ [type]: link.replace('{language}', language).replace('{country}', country.toLowerCase()) } }
    }, {})

    return {
        adjustment,
        orderRef,
        shippingAddress,
        shippingOption,
        shippingOptionJit,
        shippingOptionInfo,
        shippingOptionInfoJit,
        billingAddress,
        paymentOption,
        cardTypeInfo,
        userStatus,
        lineItems,
        numItems,
        totals,
        deviceType,
        email,
        analytics,
        locale,
        genericTotal,
        currentPackagingOption,
        saleItemsInOrder,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics: {
            ...checkoutAnalytics,
            paymentOption: paymentsOptionsList,
            shippingAddress,
            shippingOption,
            orderRef,
        },
        sustainabilityEnabled,
        buyapowaConfig,
        promotionCode,
        donationAvailable,
        donationCharities,
        donationOrderId,
        donationStatus,
        enableDonationGiving,
        crisisMessages,
        isGift,
        hasJITItems,
        remoteItems,
        localItems,
        postSplitEnabled,
        deliveryDuty,
        dduAcceptance,
        isPostSplit,
        links: parsedLinks,
        gdprLikeCountries,
        ageConsentCountries,
        tcConsentCountries,
        enableGenderSelection,
        orderConfirmationDevMode,
        minimumPriceCountries,
        discount15OffCountries,
        tierExpansionEnabled,
    }
}

export function getOrderRef(state): string {
    const { cart = {} as ShoppingCart } = state
    return cart.customerOrderNumber
}

export function getDeviceType(state: State): string {
    const { deviceType = '' } = state
    return deviceType
}

export function getUserEmail(state): string {
    const { cart = {} as ShoppingCart } = state
    return cart.x_selfEmailAddress
}

export function getDonationStatus(state) {
    const { donateToCharity } = state
    return { ...donateToCharity }
}
